/**
 * Guide Dashboard Page
 *
 * Dashboard for guides to view their performance metrics, bookings, and analytics.
 * Uses the DashboardLayout component for consistent structure across all dashboards.
 *
 * Epic: MVP 1.12 - Guide Management Dashboard & Analytics
 * Enhanced: MVP 1.21 - SCRUM-150 - Approval status banner and requirements checklist
 * Updated: MVP 1.21.2 - Dashboard Design System (SCRUM-459)
 *
 * @version 2.2
 * @created 2025-11-23
 * @updated 2026-01-16
 */

import React, { useState, useEffect } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  Calendar,
  CalendarX2,
  Users as _Users,
  TrendingUp as _TrendingUp,
  DollarSign,
  Star
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ApprovalStatusBanner from '../../components/guide/ApprovalStatusBanner';
import RequirementsChecklist from '../../components/guide/RequirementsChecklist';
import SignupStatusBanner from '../../components/guide/SignupStatusBanner';
import ActionItemsPanel from '../../components/dashboard/ActionItemsPanel';
import ReviewSummaryCard from '../../components/dashboard/ReviewSummaryCard';
import KPICard from '../../components/common/KPICard';
import ActivityTimelineWidget from '../../components/guide/ActivityTimelineWidget';
import OnboardingProgressWidget from '../../components/guide/OnboardingProgressWidget';
import ShareProfileQrCard from '../../components/guide/ShareProfileQrCard';
import { formatCentsToDollars } from '../../utils/formHelpers';

export default function GuideDashboard() {
  const { overview } = useLoaderData();
  const navigate = useNavigate();

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // State for approval status and requirements (MVP 1.21 - SCRUM-150)
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [showRequirements, setShowRequirements] = useState(false);
  const [loadingApproval, setLoadingApproval] = useState(true);
  const [loadingRequirements, setLoadingRequirements] = useState(false);

  // State for signup status banner (SCRUM-1860)
  const [signupStatus, setSignupStatus] = useState(null);

  // State for action items (MVP 1.21.3 - SCRUM-01)
  const [actionItems, setActionItems] = useState([]);
  const [loadingActionItems, setLoadingActionItems] = useState(true);
  const [actionItemsError, setActionItemsError] = useState(null);

  // Fetch signup status for banner (SCRUM-1860)
  useEffect(() => {
    const fetchSignupStatus = async () => {
      try {
        const response = await apiRequest('/api/v1/dashboard/onboarding-progress', { method: 'GET' });
        if (response.success) {
          setSignupStatus(response.data);
        }
      } catch {
        // Non-blocking — banner just won't show
      }
    };
    fetchSignupStatus();
  }, []);

  // Fetch approval status on mount
  useEffect(() => {
    const fetchApprovalStatus = async () => {
      try {
        setLoadingApproval(true);
        const response = await apiRequest('/api/v1/dashboard/approval-status', {
          method: 'GET'
        });
        if (response.success) {
          setApprovalStatus(response.data);
        }
      } catch (error) {
        console.error('Error fetching approval status:', error);
      } finally {
        setLoadingApproval(false);
      }
    };

    fetchApprovalStatus();
  }, []);

  // Fetch action items on mount (MVP 1.21.3 - SCRUM-01)
  useEffect(() => {
    const fetchActionItems = async () => {
      try {
        setLoadingActionItems(true);
        setActionItemsError(null);
        const response = await apiRequest('/api/v1/dashboard/action-items', {
          method: 'GET'
        });
        if (response.success) {
          setActionItems(response.data);
        }
      } catch (error) {
        console.error('Error fetching action items:', error);
        if (error.status === 401) {
          setActionItemsError('Session expired. Please log in again.');
        } else {
          setActionItemsError(error.message || 'Failed to load action items');
        }
      } finally {
        setLoadingActionItems(false);
      }
    };

    fetchActionItems();
  }, []);

  // Fetch requirements when viewing them
  const handleViewRequirements = async () => {
    if (requirements.length === 0) {
      try {
        setLoadingRequirements(true);
        const response = await apiRequest('/api/v1/dashboard/requirements', {
          method: 'GET'
        });
        if (response.success) {
          setRequirements(response.data);
        }
      } catch (error) {
        console.error('Error fetching requirements:', error);
      } finally {
        setLoadingRequirements(false);
      }
    }
    setShowRequirements(true);
  };

  // Calculate metrics from API data
  const metrics = {
    upcomingBookings: overview?.booking_performance?.total_bookings || 0,
    pendingRequests: overview?.booking_performance?.pending_requests || 0, // MVP 1.21.5: Now exposed
    monthlyRevenueCents: overview?.revenue?.revenue_this_month_cents || 0,
    averageRating: overview?.customer_satisfaction?.avg_rating || 0,
    daysUnavailableThisMonth: overview?.days_unavailable_this_month || 0, // SCRUM-1159
  };

  // Calculate growth percentage
  const revenueGrowth = overview?.growth?.revenue_growth_rate || 0;

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="dashboard"
      onNavigate={handleNavigate}
      user={{
        name: 'Guide User',
        email: 'guide@outdoorguides.com',
        avatar: null
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        {/* Page Header */}
        <div>
          <h1 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-2)'
          }}>
            Dashboard
          </h1>
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)'
          }}>
            Welcome back! Here&apos;s what&apos;s happening with your experiences.
          </p>
        </div>

        {/* Signup Status Banner (SCRUM-1860) */}
        {signupStatus && (
          <SignupStatusBanner
            signupStage={signupStatus.signupStage}
            signupComplete={signupStatus.signupComplete}
            pendingVerification={signupStatus.pendingVerification}
            isApproved={signupStatus.isApproved}
            stripeOnboardingComplete={signupStatus.stripeOnboardingComplete}
          />
        )}

        {/* Approval Status Banner (MVP 1.21 - SCRUM-150) */}
        <ApprovalStatusBanner
          status={approvalStatus}
          onViewRequirements={handleViewRequirements}
          loading={loadingApproval}
        />

        {/* Requirements Checklist (MVP 1.21 - SCRUM-150) */}
        {showRequirements && (
          <RequirementsChecklist
            requirements={requirements}
            loading={loadingRequirements}
          />
        )}

        {/* Action Items Panel (MVP 1.21.3 - SCRUM-01) */}
        <ActionItemsPanel
          actionItems={actionItems}
          loading={loadingActionItems}
          error={actionItemsError}
          onNavigate={handleNavigate}
        />

        {/* Onboarding Progress Widget (MVP 1.21.5 - SCRUM-530) */}
        <OnboardingProgressWidget
          guideId={overview?.guide_profile?.id}
        />

        {/* Activity Timeline & Share My Profile (MVP 1.37 - SCRUM-1820) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ActivityTimelineWidget
            guideId={overview?.guide_profile?.id}
            onViewAll={() => handleNavigate('activity', '/guide/activity')}
          />
          <ShareProfileQrCard guideId={overview?.guide_profile?.id} />
        </div>

        {/* Metrics Grid (MVP 1.21.3 - SCRUM-02: KPI Card Navigation) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* KPI Card 1 - Upcoming Bookings */}
          <KPICard
            label="Upcoming Bookings"
            value={metrics.upcomingBookings}
            change={{ value: 'Next 7 days', trend: 'neutral' }}
            icon={Calendar}
            color="success"
            onClick={() => handleNavigate('bookings', '/guide/bookings')}
          />

          {/* KPI Card 2 - Pending Requests */}
          <KPICard
            label="Pending Requests"
            value={metrics.pendingRequests}
            change={{ value: 'Needs response', trend: 'neutral' }}
            icon={_Users}
            color="warning"
            onClick={() => handleNavigate('requests', '/guide/bookings?filter=pending')}
          />

          {/* KPI Card 3 - Monthly Revenue */}
          <KPICard
            label="Monthly Revenue"
            value={formatCentsToDollars(metrics.monthlyRevenueCents)}
            change={{
              value: `${revenueGrowth >= 0 ? '+' : ''}${revenueGrowth.toFixed(1)}% from last month`,
              trend: revenueGrowth >= 0 ? 'up' : 'down'
            }}
            icon={DollarSign}
            color="primary"
            onClick={() => handleNavigate('revenue', '/guide/earnings')} // Fixed: Use correct route
          />

          {/* KPI Card 4 - Average Rating */}
          <KPICard
            label="Average Rating"
            value={metrics.averageRating.toFixed(1)}
            change={{ value: `${overview?.customer_satisfaction?.total_reviews || 0} reviews`, trend: 'neutral' }}
            icon={Star}
            color="warning"
            onClick={() => handleNavigate('reviews', '/guide/reviews')}
          />

          {/* KPI Card 5 - Days Unavailable (SCRUM-1159, SCRUM-1477) */}
          <KPICard
            label="Days unavailable"
            value={metrics.daysUnavailableThisMonth}
            change={{ value: 'This month', trend: 'neutral' }}
            icon={CalendarX2}
            color="info"
            onClick={() => handleNavigate('availability', '/guide/availability')}
          />
        </div>

        {/* Review Summary Card (MVP 1.21.3 - SCRUM-09) */}
        <ReviewSummaryCard
          satisfaction={overview?.customer_satisfaction}
          recentReviews={overview?.recent_reviews}
          loading={!overview}
          onViewAll={() => handleNavigate('reviews', '/guide/reviews')}
        />
      </div>
    </DashboardLayout>
  );
}
